package com.milta.core.mvi

import com.milta.core.observability.ErrorReporting
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/**
 * [dispatcher] runs every `scope.launch` of the ViewModel. Production keeps [Dispatchers.Default];
 * a test passes the runTest scheduler's `UnconfinedTestDispatcher`, so an intent's work is done
 * before `onIntent` returns and the test never races the ViewModel's own thread.
 */
abstract class MviViewModel<S, I>(initialState: S, dispatcher: CoroutineDispatcher = Dispatchers.Default) {

    private val _state = MutableStateFlow(initialState)
    val state: StateFlow<S> = _state.asStateFlow()

    // Last-resort net for exceptions escaping a `scope.launch`: reported instead of crashing the app.
    protected val scope = CoroutineScope(
        SupervisorJob() + dispatcher +
            CoroutineExceptionHandler { _, throwable -> ErrorReporting.report(throwable, source = "mvi-scope") },
    )

    protected val currentState: S get() = _state.value

    abstract fun onIntent(intent: I)

    protected fun setState(reducer: S.() -> S) {
        _state.update(reducer)
    }

    /** For `onFailure` blocks: reports [throwable] if unexpected (4xx, offline and cancellation are filtered out). */
    protected fun reportFailure(throwable: Throwable) {
        ErrorReporting.report(throwable, source = this::class.simpleName)
    }

    fun clear() {
        scope.cancel()
    }
}
